import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  TextField,
  Divider,
  Alert
} from '@mui/material';
import {
  Delete,
  ShoppingBag,
  Add,
  Remove,
  ArrowBack,
  WhatsApp
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import Header from '../components/common/Header';
import ValEdIcon from '../components/common/ValEdIcon';
import { removeFromCart, updateQuantity, clearCart } from '../store/cartSlice';
import ordersService from '../services/orders.service';
import whatsappService from '../services/whatsapp.service';
import { formatPriceWithCurrency } from '../utils/formatPrice';

const Cart = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const { items, totalItems, totalPrice } = useSelector((state) => state.cart);
  const { user } = useSelector((state) => state.auth);
  
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [phone, setPhone] = useState(user?.phone || '');
  const [notes, setNotes] = useState('');
  const [ordering, setOrdering] = useState(false);

  // Vérifier si le ValEd contient des produits physiques
  const hasPhysicalProducts = items.some(item => item.product.product_type === 'physical');
  
  // Vérifier si des quantités dépassent le stock
  const hasStockIssues = items.some(item => 
    item.product.stock !== null && item.quantity > item.product.stock
  );

  const handleRemove = (productId) => {
    dispatch(removeFromCart(productId));
    toast.success('Produit retiré de la  ValEd');
  };

  const handleUpdateQuantity = (productId, newQuantity) => {
    if (newQuantity < 1) return;
    
    // Trouver le produit pour vérifier le stock
    const cartItem = items.find(item => item.product.id === productId);
    if (cartItem && cartItem.product.stock !== null) {
      if (newQuantity > cartItem.product.stock) {
        toast.warning(`Quantité limitée à ${cartItem.product.stock} (stock disponible)`);
        return;
      }
    }
    
    dispatch(updateQuantity({ productId, quantity: newQuantity }));
  };

  const handleClearCart = () => {
    if (confirm('Voulez-vous vraiment vider la ValEd ?')) {
      dispatch(clearCart());
      toast.success('ValEd vidé');
    }
  };

  const handleCheckout = async () => {
    if (!user) {
      toast.error('Vous devez être connecté pour commander');
      navigate('/auth');
      return;
    }

    if (hasPhysicalProducts && !deliveryAddress.trim()) {
      toast.error('Veuillez fournir une adresse de livraison');
      return;
    }

    if (!phone.trim()) {
      toast.error('Veuillez fournir un numéro de téléphone');
      return;
    }

    setOrdering(true);

    try {
      const token = localStorage.getItem('access_token');
      
      // Créer une commande pour chaque produit du ValEd
      const orderPromises = items.map(item => 
        ordersService.createOrder({
          product_id: item.product.id,
          quantity: item.quantity,
          delivery_address: hasPhysicalProducts ? deliveryAddress : undefined,
          phone,
          notes
        }, token)
      );

      await Promise.all(orderPromises);

      // Vider le ValEd après succès
      dispatch(clearCart());

      toast.success('Commande passée avec succès ! Vous allez recevoir un email de confirmation.');
      navigate('/orders');
    } catch (error) {
      console.error('Erreur lors de la commande:', error);
      toast.error(error.response?.data?.error || 'Erreur lors de la commande');
    } finally {
      setOrdering(false);
    }
  };

  const handleWhatsAppOrder = async () => {
    // Filtrer les produits physiques uniquement
    const physicalItems = items.filter(item => item.product.product_type === 'physical');
    
    if (physicalItems.length === 0) {
      toast.error('  Aucun produit physique dans la ValEd');
      return;
    }

    // Vérifier l'authentification (EXACTEMENT comme ArticleDetails)
    const token = localStorage.getItem('access_token');
    if (!token) {
      toast.error('Vous devez être connecté pour passer une commande');
      navigate('/auth');
      return;
    }

    // Vérifier le stock pour tous les produits (EXACTEMENT comme ArticleDetails)
    for (const item of physicalItems) {
      const product = item.product;
      if (product.stock !== null) {
        if (product.stock === 0) {
          toast.error('  Produit en rupture de stock');
          return;
        }
        if (item.quantity > product.stock) {
          toast.error(`  Stock insuffisant ! Disponible: ${product.stock}, Demandé: ${item.quantity}`);
          return;
        }
      }
    }

    // Créer les commandes une par une (EXACTEMENT comme ArticleDetails)
    try {
      for (const item of physicalItems) {
        // Créer la commande dans la base de données (EXACTEMENT comme ArticleDetails)
        const orderData = {
          product_id: item.product.id,
          quantity: item.quantity,
          delivery_address: user?.address || 'Adresse à préciser via WhatsApp',
          phone: user?.phone || 'Téléphone à préciser via WhatsApp',
          notes: 'Commande passée via WhatsApp - Détails à confirmer',
          order_source: 'whatsapp'
        };
        
        console.log('📦 Données de commande:', orderData);
        toast.info('⏳ Création de la commande...');
        
        const createdOrder = await ordersService.createOrder(orderData);
        console.log('   Commande créée:', createdOrder);

        // Ouvrir WhatsApp avec le message (EXACTEMENT comme ArticleDetails)
        whatsappService.openWhatsAppOrder(item.product, item.quantity, user);
      }
      
      toast.success('   Commande créée avec succès ! Redirection vers WhatsApp...');
      
      // Vider le ValEd après succès
      dispatch(clearCart());
      
      // Optionnel : rediriger vers mes commandes après un délai (EXACTEMENT comme ArticleDetails)
      setTimeout(() => {
        navigate('/orders');
      }, 3000);
      
    } catch (error) {
      console.error('  Erreur création commande:', error);
      console.error('  Détails erreur:', error.response?.data);
      
      if (error.status === 400) {
        toast.error(`  ${error.data?.error || 'Erreur de validation'}`);
      } else if (error.status === 401) {
        toast.error('  Session expirée, veuillez vous reconnecter');
        navigate('/auth');
      } else {
        toast.error(`  Erreur lors de la création de la commande: ${error.message}`);
      }
    }
  };

  if (items.length === 0) {
    return (
      <Box  className="mobile-app-container has-fixed-header" sx={{ bgcolor: "#FFF7E5", minHeight: '100vh' }}>
        <Header />
        <Container sx={{ py: 4, textAlign: 'center' }}>
          <Typography variant="h4" sx={{ mb: 2, fontFamily: 'Achiko', color: '#784e3c', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
             <ValEdIcon size={32} color="#8B4513" /> ValEd vide
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            Votre ValEd est vide. Découvrez nos produits !
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/articles')}
            sx={{
              bgcolor: '#784e3c',
              fontFamily: 'Achiko',
              color: '#FFFFFF',
              textTransform: 'none',
              borderRadius: '999px',
                            
              '&:hover': { bgcolor: '#3a9bc7' }
            }}
          >
            Voir la boutique
          </Button>
        </Container>
      </Box>
    );
  }

  return (
    <Box className="mobile-app-container has-fixed-header"  sx={{ bgcolor: "#FFF7E5", minHeight: '100vh' }}>
      <Header />
      
      <Container sx={{ py: { xs: 2, md: 4 } }}>
        {/* En-tête */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2, mb: 4 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/articles')}
            sx={{ color: '#784e3c', fontSize: { xs: '0.875rem', sm: '1rem' } }}
          >
            Continuer mes achats
          </Button>
          
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={handleClearCart}
            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
          >
            Vider la ValEd
          </Button>
        </Box>

        <Typography
          variant="h4"
          sx={{
            fontWeight: 'bold',
            fontFamily: 'Achiko',
            color: '#784e3c',
            mb: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' }
          }}
        >
          <ValEdIcon size={32} color="#8B4513" />
          Ma ValEd ({totalItems} {totalItems > 1 ? 'articles' : 'article'})
        </Typography>

        <Grid container spacing={4}>
          {/* Liste des produits */}
          <Grid item xs={12} md={8}>
            {items.map((item) => (
              <Card key={item.product.id} sx={{ mb: 2 }}>
                <CardContent>
                  <Grid container spacing={2} alignItems="center">
                    {/* Image */}
                    <Grid item xs={12} sm={3}>
                      <img
                        src={item.product.image_url || 'https://via.placeholder.com/150x150?text=Produit'}
                        alt={item.product.title}
                        style={{
                          width: '100%',
                          height: '100px',
                          objectFit: 'cover',
                          borderRadius: '8px'
                        }}
                      />
                    </Grid>

                    {/* Détails */}
                    <Grid item xs={8} sm={5}>
                      <Typography variant="h6" sx={{ fontFamily: 'Achiko', fontWeight: 'bold' }}>
                        {item.product.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {item.product.product_type === 'physical' ? ' Physique' : ' Virtuel'}
                      </Typography>
                      <Typography variant="h6" sx={{ color: '#784e3c', fontWeight: 'bold', mt: 1 }}>
                        {formatPriceWithCurrency(item.product.price)}
                      </Typography>
                    </Grid>

                    {/* Quantité */}
                    <Grid item xs={3} sm={3}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleUpdateQuantity(item.product.id, item.quantity - 1)}
                          sx={{ bgcolor: 'rgba(67, 172, 217, 0.1)' }}
                        >
                          <Remove fontSize="small" />
                        </IconButton>
                        <Typography sx={{ minWidth: 30, textAlign: 'center', fontWeight: 'bold' }}>
                          {item.quantity}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => handleUpdateQuantity(item.product.id, item.quantity + 1)}
                          disabled={item.product.stock !== null && item.quantity >= item.product.stock}
                          sx={{ 
                            bgcolor: 'rgba(67, 172, 217, 0.1)',
                            '&.Mui-disabled': {
                              bgcolor: 'rgba(0, 0, 0, 0.1)',
                              color: 'rgba(0, 0, 0, 0.3)'
                            }
                          }}
                        >
                          <Add fontSize="small" />
                        </IconButton>
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                        Total: {formatPriceWithCurrency(item.product.price * item.quantity)}
                      </Typography>
                      {item.product.stock !== null && item.quantity > item.product.stock && (
                        <Typography variant="caption" color="error" sx={{ display: 'block', textAlign: 'center', fontWeight: 'bold' }}>
                          ⚠️ Quantité {'>'}  stock ({item.product.stock})
                        </Typography>
                      )}
                    </Grid>

                    {/* Supprimer */}
                    <Grid item xs={1} sm={1}>
                      <IconButton
                        color="error"
                        onClick={() => handleRemove(item.product.id)}
                      >
                        <Delete />
                      </IconButton>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))}
          </Grid>

          {/* Récapitulatif et formulaire */}
          <Grid item xs={12} md={4}>
            <Card sx={{ position: { xs: 'relative', md: 'sticky' }, top: { xs: 0, md: 20 } }}>
              <CardContent>
                <Typography variant="h5" sx={{ mb: 3, fontFamily: 'Achiko', fontWeight: 'bold' }}>
                  Récapitulatif
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography>Sous-total:</Typography>
                    <Typography fontWeight="bold">{formatPriceWithCurrency(totalPrice)}</Typography>
                  </Box>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h6" fontWeight="bold">Total:</Typography>
                    <Typography variant="h6" fontWeight="bold" sx={{ color: '#784e3c' }}>
                      {formatPriceWithCurrency(totalPrice)}
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Bouton WhatsApp uniquement */}
                {items.some(item => item.product.product_type === 'physical') && (
                  <Box>
                    {/* Message d'information */}
                    <Alert severity="info" sx={{ mb: 3, fontSize: '0.9rem' }}>
                      <strong>Commander via WhatsApp :</strong> Votre commande sera créée automatiquement dans "Mes commandes" puis vous serez redirigé vers WhatsApp pour finaliser les détails de livraison avec notre équipe.
                    </Alert>
                    
                    <Button
                      variant="contained"
                      fullWidth
                      size="large"
                      startIcon={<WhatsApp />}
                      onClick={handleWhatsAppOrder}
                      disabled={hasStockIssues}
                      sx={{
                        bgcolor: hasStockIssues ? '#ccc' : '#25D366',
                        py: 1.5,
                        fontFamily: 'Achiko',
                        fontWeight: 'bold',
                        color: '#FFFFFF',
                        textTransform: 'none',
                        borderRadius: '999px',
                        justifyContent: 'center',
                        '&:hover': { bgcolor: hasStockIssues ? '#ccc' : '#20BA5A' },
                        '&.Mui-disabled': {
                          bgcolor: '#ccc',
                          color: '#777'
                        }
                      }}
                    >
                      {hasStockIssues ? 'Vérifiez les quantités' : 'Commander via WhatsApp'}
                    </Button>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                      Commande directe avec notre équipe - Détails à confirmer sur WhatsApp
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Cart;
